import { experienceLabel, genderLabel } from "../../utils/hhLabels";
import { formatTelegramUsername, telegramHref, formatEmail } from "../../utils/candidateMapper";
import { parseWorkExperienceList } from "../../utils/candidateActivity";
import { getCandidateNumerology } from "../../utils/candidateNumerology";
import { formatDateRu, formatDateTimeRu } from "../../utils/dateFormat";
import { Chip, initials } from "./CandidateProfileCard";

/**
 * Wide, HH-like candidate resume: a compact header and label/value rows read
 * top to bottom, so HR sees everything on one screen while on a call.
 */

function asList(value) {
    if (value == null || value === "") return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value];
}

function jobsOf(candidate) {
    if (Array.isArray(candidate?.experience) && candidate.experience.length) {
        return candidate.experience.filter(Boolean);
    }
    return parseWorkExperienceList(candidate?.other_work_expirience);
}

export function Row({ label, children, testId }) {
    if (children == null || children === "" || children === false) return null;
    return (
        <div className="grid grid-cols-[150px_minmax(0,1fr)] gap-4 border-b border-slate-100 py-2 last:border-b-0 xl:grid-cols-[180px_minmax(0,1fr)]" data-testid={testId}>
            <dt className="text-sm text-slate-500">{label}</dt>
            <dd className="min-w-0 break-words text-sm text-slate-900">{children}</dd>
        </div>
    );
}

export function Block({ title, children, aside }) {
    return (
        <section className="rounded-xl border border-slate-200 bg-white px-5 py-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <h3 className="text-base font-bold text-slate-900">{title}</h3>
                {aside}
            </div>
            {children}
        </section>
    );
}

export default function CandidateResumeWide({
    candidate,
    vacancyTitle,
    showAi = true,
    badges,
    actions,
    belowHeader,
    tabs,
    resumeHidden = false,
}) {
    const hardSkills = asList(candidate.hard_skills);
    const programs = asList(candidate.work_programs);
    const languages = asList(candidate.languages);
    const hobbies = asList(candidate.hobbies);
    const jobs = jobsOf(candidate);
    const education = asList(candidate.education);
    const aiScore = candidate.ai_score;
    const hasAiScore = aiScore !== null && aiScore !== undefined;
    const birth = candidate.birth_date || candidate.birthdate;
    const numerology = getCandidateNumerology(birth);
    const birthWithNumerology = birth ? `${formatDateRu(birth)}, ${numerology.cs ?? "—"}/${numerology.cm ?? "—"}` : null;
    const telegram = formatTelegramUsername(candidate.telegram_username);
    const email = formatEmail(candidate.email);
    const salary = candidate.salary_expectations
        ? `${Number(candidate.salary_expectations).toLocaleString("ru-RU")} ₽`
        : candidate.salary_label || null;
    const activeSearch = candidate.active_search === true
        ? "Да"
        : candidate.active_search === false ? "Нет" : candidate.job_search_status || null;
    const totalExperience = experienceLabel(candidate.total_work_expirience)
        || candidate.total_work_expirience
        || candidate.experience_label;

    return (
        <div className="space-y-4" data-testid="candidate-resume-wide">
            <header className="rounded-xl border border-slate-200 bg-white px-5 py-4">
                <div className="flex flex-wrap items-start gap-4">
                    {candidate.photo_url ? (
                        <img src={candidate.photo_url} alt={candidate.full_name} className="h-16 w-16 shrink-0 rounded-xl object-cover ring-1 ring-slate-200" />
                    ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#cda834]/20 text-lg font-bold text-slate-800 ring-1 ring-[#cda834]/30">
                            {initials(candidate.full_name)}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <h2 className="text-xl font-bold tracking-tight text-slate-900">{candidate.full_name || "Кандидат"}</h2>
                            <div className="flex flex-wrap gap-1.5">{badges}</div>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                            {vacancyTitle
                                ? <>Вакансия: <span className="font-medium text-slate-800">{vacancyTitle}</span></>
                                : <span className="text-slate-400">Вакансия не привязана</span>}
                            {salary ? <span className="ml-3">Ожидания: <b className="text-slate-800">{salary}</b></span> : null}
                            {(candidate.area || candidate.city) ? <span className="ml-3">{candidate.area || candidate.city}</span> : null}
                            {candidate.age ? <span className="ml-3">{candidate.age} лет</span> : null}
                        </p>
                        <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                            {candidate.phone_number ? <a href={`tel:${candidate.phone_number}`} className="font-semibold text-slate-900 hover:underline">{candidate.phone_number}</a> : null}
                            {telegram ? <a href={telegramHref(telegram)} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{telegram}</a> : null}
                            {email ? <a href={`mailto:${email}`} className="text-sky-700 hover:underline">{email}</a> : null}
                        </p>
                    </div>
                    {actions ? <div className="flex flex-wrap justify-end gap-2 xl:max-w-[50%]">{actions}</div> : null}
                </div>
                {belowHeader ? <div className="mt-3 space-y-3">{belowHeader}</div> : null}
            </header>

            {tabs}

            {!resumeHidden && showAi ? (
                <section className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3">
                    <div className="flex items-end gap-1">
                        <span className="text-2xl font-bold tabular-nums text-slate-900" data-testid="candidate-ai-score">
                            {hasAiScore ? Number(aiScore).toFixed(0) : "—"}
                        </span>
                        <span className="mb-0.5 text-sm text-slate-400">/ 100</span>
                    </div>
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#cda834]" style={{ width: `${hasAiScore ? Math.min(100, Math.max(0, Number(aiScore))) : 0}%` }} />
                    </div>
                    <p className="min-w-[240px] flex-1 text-sm leading-relaxed text-slate-700" data-testid="candidate-ai-comment">
                        {candidate.ai_comment || "Комментария AI пока нет."}
                    </p>
                </section>
            ) : null}

            {resumeHidden ? null : <>
            <Block title="Основное">
                <dl>
                    <Row label="Пол">{genderLabel(candidate.gender) || candidate.gender}</Row>
                    <Row label="Дата рождения, ЧС/ЧМ">{birthWithNumerology}</Row>
                    <Row label="Дата отклика">{formatDateTimeRu(candidate.applied_at, null)}</Row>
                    <Row label="Активный поиск">{activeSearch}</Row>
                    <Row label="Резюме обновлено">{candidate.resume_updated_label || formatDateRu(candidate.resume_update_date, null)}</Row>
                    <Row label="Семья">{candidate.marital_status}</Row>
                </dl>
            </Block>

            {candidate.feedback ? (
                <Block title="Отзыв кандидата"><p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{candidate.feedback}</p></Block>
            ) : null}
            {candidate.about ? (
                <Block title="О себе"><p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{candidate.about}</p></Block>
            ) : null}

            <Block
                title="Опыт работы"
                aside={totalExperience ? <span className="text-sm text-slate-500">Всего: <b className="text-slate-800">{totalExperience}</b></span> : null}
            >
                <dl>
                    <Row label="На релевантной позиции">{candidate.relevant_position_expirience}</Row>
                    <Row label="На конкретной позиции">{candidate.certain_position_expirience}</Row>
                    <Row label="Средний стаж">{candidate.average_service_length ? `${candidate.average_service_length} лет` : null}</Row>
                </dl>
                {jobs.length ? (
                    <ol className="mt-2 divide-y divide-slate-100">
                        {jobs.map((job, index) => (
                            <li key={index} className="grid grid-cols-[150px_minmax(0,1fr)] gap-4 py-3 xl:grid-cols-[180px_minmax(0,1fr)]">
                                <div className="text-sm text-slate-500">{job.period || "—"}</div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">{job.title || "Опыт работы"}</p>
                                    {job.company ? <p className="text-sm text-slate-700">{job.company}</p> : null}
                                    {(job.industry || job.region) ? <p className="text-xs text-slate-500">{[job.industry, job.region].filter(Boolean).join(" · ")}</p> : null}
                                    {job.description ? <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{job.description}</p> : null}
                                </div>
                            </li>
                        ))}
                    </ol>
                ) : null}
            </Block>

            {(education.length || hardSkills.length || programs.length || languages.length || hobbies.length || candidate.personal_characteristics) ? (
                <Block title="Образование и навыки">
                    <dl>
                        <Row label="Образование">
                            {education.length ? <ul className="space-y-1">{education.map((item, index) => <li key={index}>{item}</li>)}</ul> : null}
                        </Row>
                        <Row label="Навыки">
                            {hardSkills.length ? <div className="flex flex-wrap gap-1.5">{hardSkills.map((skill, index) => <Chip key={index} tone="sky">{skill}</Chip>)}</div> : null}
                        </Row>
                        <Row label="Программы">
                            {programs.length ? <div className="flex flex-wrap gap-1.5">{programs.map((prog, index) => <Chip key={index} tone="violet">{prog}</Chip>)}</div> : null}
                        </Row>
                        <Row label="Языки">{languages.length ? languages.join(", ") : null}</Row>
                        <Row label="Личные качества">{candidate.personal_characteristics}</Row>
                        <Row label="Хобби">{hobbies.length ? hobbies.join(", ") : null}</Row>
                    </dl>
                </Block>
            ) : null}
            </>}
        </div>
    );
}
